import styled from '@emotion/styled';

import { Chip, StarRating } from '@travelnest/hui';

import { Stack } from '@/components/ui/Stack';
import { Text } from '@/components/ui/Text';
import { MAX_STARS, type ReviewStats, ratingAdjective } from '@/lib/rating';
import { colors, font, mq, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';

import { FlexGraph } from './FlexGraph';

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacing.space50};

	${mq.md} {
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;
	}
`;

const Score = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacing.space20};

	strong {
		${font('RWB140', '100%')}
		color: ${colors.typography.heading};
	}
`;

interface RatingBreakdownProps {
	stats: ReviewStats;
}

/** Average score, stars and adjective on the left; how the stars split on the right. */
export const RatingBreakdown = ({ stats }: RatingBreakdownProps) => {
	const t = useI18n();
	const adjective = ratingAdjective(stats.averageRating);
	const bars = stats.ratingScoreCount
		.map((count, i) => ({
			count,
			annotation: (
				<StarRating
					total={i + 1}
					filled={i + 1}
					shadow="NONE"
					range="TRUNCATED"
					size="small"
				/>
			),
			altText: `${i + 1} ${t('property.reviews.starReviews')}`,
		}))
		.reverse();

	return (
		<Wrapper data-testid="review-breakdown">
			<Stack gap="space20" align="start">
				<Score>
					<strong>{stats.averageRating.toFixed(1)}</strong>
					<StarRating
						total={MAX_STARS}
						filled={stats.averageRating}
					/>
				</Score>
				{adjective && (
					<Chip
						text={t(`property.reviews.${adjective}`)}
						color="green"
					/>
				)}
				<Text variant="bodySmall">
					{t('property.reviews.count', { count: stats.count })}
				</Text>
			</Stack>
			<FlexGraph items={bars} />
		</Wrapper>
	);
};
